import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox, Snackbar, Alert } from '@mui/material';
import { useAuth } from '../AuthContext';
import { fetchAllFishSpecies, createFishSpecies, updateFishSpecies, deleteFishSpecies } from '../api';

export default function AdminSpeciesPage() {
  const { token } = useAuth();
  const [species, setSpecies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [newOrder, setNewOrder] = useState('');
  const [newMultiplier, setNewMultiplier] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showMessage = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchAllFishSpecies(token);
        setSpecies(Array.isArray(data) ? data : []);
      } catch (err) {
        showMessage('Fehler beim Laden der Fischarten', 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleAdd = async () => {
    if (!newName) return;
    try {
      const order = newOrder === '' ? species.length : parseInt(newOrder, 10) || 0;
      const multiplier = newMultiplier === '' ? 1 : parseFloat(newMultiplier) || 1;
      const created = await createFishSpecies(token, { name: newName, order, is_active: true, points_multiplier: multiplier });
      setSpecies((prev) => [...prev, created].sort((a, b) => (a.order - b.order) || a.name.localeCompare(b.name)));
      setNewName('');
      setNewOrder('');
      setNewMultiplier('');
      showMessage('Fischart hinzugefügt');
    } catch (err) {
      showMessage('Fehler beim Hinzufügen', 'error');
    }
  };

  const handleUpdateField = (id, field, value) => {
    setSpecies((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  const handleSave = async (s) => {
    try {
      await updateFishSpecies(token, s.id, {
        name: s.name,
        order: s.order,
        is_active: s.is_active,
        points_multiplier: s.points_multiplier,
      });
      showMessage('Gespeichert');
    } catch (err) {
      showMessage('Fehler beim Speichern', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteFishSpecies(token, id);
      setSpecies((prev) => prev.filter((s) => s.id !== id));
      showMessage('Gelöscht');
    } catch (err) {
      showMessage('Fehler beim Löschen', 'error');
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Fischarten verwalten
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="Neue Fischart (deutscher Name)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          fullWidth
        />
        <TextField
          label="Reihenfolge"
          type="number"
          value={newOrder}
          onChange={(e) => setNewOrder(e.target.value)}
          sx={{ width: 140 }}
        />
        <TextField
          label="Punkte-Faktor"
          type="number"
          value={newMultiplier}
          onChange={(e) => setNewMultiplier(e.target.value)}
          sx={{ width: 140 }}
        />
        <Button variant="contained" onClick={handleAdd} disabled={!newName || loading}>
          Hinzufügen
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Reihenfolge</TableCell>
              <TableCell>Punkte-Faktor</TableCell>
              <TableCell>Aktiv</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {species.map((s) => (
              <TableRow key={s.id}>
                <TableCell>
                  <TextField
                    value={s.name}
                    onChange={(e) => handleUpdateField(s.id, 'name', e.target.value)}
                    variant="standard"
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={s.order}
                    onChange={(e) => handleUpdateField(s.id, 'order', parseInt(e.target.value, 10) || 0)}
                    variant="standard"
                    sx={{ width: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={s.points_multiplier}
                    onChange={(e) => handleUpdateField(s.id, 'points_multiplier', parseFloat(e.target.value) || 0)}
                    variant="standard"
                    sx={{ width: 120 }}
                  />
                </TableCell>
                <TableCell>
                  <Checkbox
                    checked={s.is_active}
                    onChange={(e) => handleUpdateField(s.id, 'is_active', e.target.checked)}
                  />
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => handleSave(s)} sx={{ mr: 1 }}>
                    Speichern
                  </Button>
                  <Button size="small" color="error" onClick={() => handleDelete(s.id)}>
                    Löschen
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
